import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import {
  fetchAiSystemSettings,
  fetchFeatureSwitches,
  fetchPublicSettings,
  fetchSystemSettings,
  patchAiSystemSettings,
  patchFeatureSwitches,
  patchSystemSettings,
} from '@/api/client'
import type { AiChannelToggles, AiSystemSettings } from '@/api/types'
import { ApiError } from '@/api/client'
import { isAdminUser } from '@/api/types'
import { useAuth } from '@/auth/useAuth'

import { Settings } from 'lucide-react'
import { AdminSection } from '@/features/admin/AdminSection'
function readCurrencyCode(settings: Record<string, unknown> | undefined): string {
  const raw = settings?.base_currency
  if (raw && typeof raw === 'object' && !Array.isArray(raw) && 'code' in raw) {
    const code = (raw as { code?: unknown }).code
    if (typeof code === 'string') return code
  }
  if (typeof raw === 'string') return raw
  return 'USD'
}

function readSenderAddress(settings: Record<string, unknown> | undefined): string {
  const raw = settings?.email_sender
  if (raw && typeof raw === 'object' && !Array.isArray(raw) && 'sender_address' in raw) {
    const addr = (raw as { sender_address?: unknown }).sender_address
    if (typeof addr === 'string') return addr
  }
  return ''
}

function formatDemoRecipients(recipients: string[]): string {
  return recipients.join('\n')
}

function parseDemoRecipients(text: string): string[] {
  return text
    .split(/[\n,]+/)
    .map((entry) => entry.trim())
    .filter(Boolean)
}

function readDemoRecipients(settings: Record<string, unknown> | undefined): string[] {
  const raw = settings?.demo_test_recipients
  if (!Array.isArray(raw)) return []
  return raw.filter((entry): entry is string => typeof entry === 'string')
}

function readSignInNote(settings: Record<string, unknown> | undefined): string {
  const raw = settings?.sign_in_settings
  if (raw && typeof raw === 'object' && !Array.isArray(raw) && 'note' in raw) {
    const note = (raw as { note?: unknown }).note
    if (typeof note === 'string') return note
  }
  return ''
}

function readRetentionPolicy(
  settings: Record<string, unknown> | undefined,
): Record<string, string> {
  const raw = settings?.retention_policy
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return { audit_log: 'indefinite', snapshots: 'indefinite' }
  }
  const policy = raw as Record<string, unknown>
  return {
    audit_log: typeof policy.audit_log === 'string' ? policy.audit_log : 'indefinite',
    snapshots: typeof policy.snapshots === 'string' ? policy.snapshots : 'indefinite',
  }
}

export function SystemAdminPage() {
  const { user } = useAuth()
  const systemAdmin = isAdminUser(user)
  const qc = useQueryClient()

  const settingsQuery = useQuery({
    queryKey: ['config', 'settings'],
    queryFn: fetchPublicSettings,
    enabled: !systemAdmin,
  })
  const systemSettingsQuery = useQuery({
    queryKey: ['config', 'system-settings'],
    queryFn: fetchSystemSettings,
    enabled: systemAdmin,
  })
  const switchesQuery = useQuery({
    queryKey: ['config', 'feature-switches'],
    queryFn: fetchFeatureSwitches,
  })
  const aiQuery = useQuery({
    queryKey: ['ai', 'settings', 'system'],
    queryFn: fetchAiSystemSettings,
    enabled: systemAdmin,
  })

  const [error, setError] = useState<string | null>(null)
  const [currency, setCurrency] = useState('USD')
  const [timezone, setTimezone] = useState('UTC')
  const [customerBarTopN, setCustomerBarTopN] = useState(10)
  const [senderAddress, setSenderAddress] = useState('')
  const [demoRecipientsText, setDemoRecipientsText] = useState('')
  const [signInNote, setSignInNote] = useState('')
  const [retentionPolicy, setRetentionPolicy] = useState<Record<string, string>>({
    audit_log: 'indefinite',
    snapshots: 'indefinite',
  })
  const [localSwitches, setLocalSwitches] = useState<Record<string, boolean>>({})
  const [aiLocal, setAiLocal] = useState<AiSystemSettings | null>(null)

  const catalogueSettings = systemAdmin
    ? systemSettingsQuery.data?.settings
    : settingsQuery.data?.settings

  useEffect(() => {
    if (!catalogueSettings) return
    setCurrency(readCurrencyCode(catalogueSettings))
    const tz = catalogueSettings.email_timezone
    if (typeof tz === 'string') setTimezone(tz)
    const topN = catalogueSettings.customer_bar_top_n
    if (typeof topN === 'number' && Number.isFinite(topN)) {
      setCustomerBarTopN(Math.min(50, Math.max(1, Math.floor(topN))))
    } else if (typeof topN === 'string' && topN.trim()) {
      const parsed = Number(topN)
      if (Number.isFinite(parsed)) {
        setCustomerBarTopN(Math.min(50, Math.max(1, Math.floor(parsed))))
      }
    }
    setSenderAddress(readSenderAddress(catalogueSettings))
    setDemoRecipientsText(formatDemoRecipients(readDemoRecipients(catalogueSettings)))
    setSignInNote(readSignInNote(catalogueSettings))
    setRetentionPolicy(readRetentionPolicy(catalogueSettings))
  }, [catalogueSettings])

  useEffect(() => {
    if (switchesQuery.data?.feature_switches) {
      setLocalSwitches({ ...switchesQuery.data.feature_switches })
    }
  }, [switchesQuery.data])

  useEffect(() => {
    if (aiQuery.data) {
      setAiLocal(aiQuery.data)
    }
  }, [aiQuery.data])

  const systemSettingsMut = useMutation({
    mutationFn: (settings: Record<string, unknown>) => patchSystemSettings(settings),
    onSuccess: async () => {
      setError(null)
      await qc.invalidateQueries({ queryKey: ['config', 'system-settings'] })
      await qc.invalidateQueries({ queryKey: ['config', 'settings'] })
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Failed to save system settings')
    },
  })

  const switchesMut = useMutation({
    mutationFn: patchFeatureSwitches,
    onSuccess: async () => {
      setError(null)
      await qc.invalidateQueries({ queryKey: ['config', 'feature-switches'] })
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Failed to update feature switches')
    },
  })

  const aiMut = useMutation({
    mutationFn: patchAiSystemSettings,
    onSuccess: async () => {
      setError(null)
      await qc.invalidateQueries({ queryKey: ['ai', 'settings', 'system'] })
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Failed to update AI settings')
    },
  })

  const switchEntries = Object.entries(localSwitches).sort(([a], [b]) => a.localeCompare(b))
  const channelEntries = aiLocal
    ? (Object.entries(aiLocal.channels) as [keyof AiChannelToggles, boolean][])
    : []

  return (
    <AdminSection
      title="System"
      tone="slate"
      icon={Settings}
      lede="Technical catalogue settings for system administrators. Feature switches require the system administrator role."
    >
      {error && (
        <p className="alert alert--error" role="alert">
          {error}
        </p>
      )}

      {!systemAdmin && (
        <p className="admin-section__lede">
          View only — system administrator role is required to change technical settings.
        </p>
      )}

      <form
        className="admin-form"
        onSubmit={(event) => {
          event.preventDefault()
          if (!systemAdmin) return
          systemSettingsMut.mutate({
            base_currency: { code: currency.trim().toUpperCase() },
            email_timezone: timezone.trim(),
            customer_bar_top_n: customerBarTopN,
          })
        }}
      >
        <h3 className="admin-form__title">Regional settings</h3>
        <div className="admin-form__row">
          <label className="field">
            <span className="field__label">Base currency</span>
            <input
              className="field__input"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              required
              maxLength={8}
              disabled={!systemAdmin || systemSettingsMut.isPending}
            />
          </label>
          <label className="field">
            <span className="field__label">Email timezone</span>
            <input
              className="field__input"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              required
              placeholder="Asia/Kolkata"
              disabled={!systemAdmin || systemSettingsMut.isPending}
            />
          </label>
          <label className="field">
            <span className="field__label">Customer bar chart top N</span>
            <input
              className="field__input"
              type="number"
              min={1}
              max={50}
              step={1}
              value={customerBarTopN}
              onChange={(e) => setCustomerBarTopN(Number(e.target.value) || 1)}
              required
              disabled={!systemAdmin || systemSettingsMut.isPending}
            />
            <span className="field__hint">
              Named customers on the opportunities bar chart; the rest roll into Others (1–50).
            </span>
          </label>
          {systemAdmin && (
            <button
              className="button button--primary"
              type="submit"
              disabled={systemSettingsMut.isPending}
            >
              Save settings
            </button>
          )}
        </div>
      </form>

      <form
        className="admin-form"
        onSubmit={(event) => {
          event.preventDefault()
          if (!systemAdmin) return
          const existing = catalogueSettings?.email_sender
          const prior =
            existing && typeof existing === 'object' && !Array.isArray(existing)
              ? (existing as Record<string, unknown>)
              : {}
          systemSettingsMut.mutate({
            email_sender: {
              ...prior,
              sender_address: senderAddress.trim(),
            },
          })
        }}
      >
        <h3 className="admin-form__title">Email sender</h3>
        <p className="admin-section__lede">
          Outbound sender address for system mail. ACS connection string is configured via
          environment or Key Vault.
        </p>
        <div className="admin-form__row">
          <label className="field">
            <span className="field__label">Sender address</span>
            <input
              className="field__input"
              type="email"
              value={senderAddress}
              onChange={(e) => setSenderAddress(e.target.value)}
              disabled={!systemAdmin || systemSettingsMut.isPending}
              placeholder="aries@example.com"
            />
          </label>
          {systemAdmin && (
            <button
              className="button button--primary"
              type="submit"
              disabled={systemSettingsMut.isPending}
            >
              Save sender
            </button>
          )}
        </div>
      </form>

      <form
        className="admin-form"
        onSubmit={(event) => {
          event.preventDefault()
          if (!systemAdmin) return
          systemSettingsMut.mutate({
            demo_test_recipients: parseDemoRecipients(demoRecipientsText),
          })
        }}
      >
        <h3 className="admin-form__title">Demo test recipients</h3>
        <p className="admin-section__lede">
          Optional allow-list for demo or test mail (comma or newline separated).
        </p>
        <label className="field">
          <span className="field__label">Recipient emails</span>
          <textarea
            className="field__input"
            rows={4}
            value={demoRecipientsText}
            onChange={(e) => setDemoRecipientsText(e.target.value)}
            disabled={!systemAdmin || systemSettingsMut.isPending}
            placeholder="demo@example.com"
          />
        </label>
        {systemAdmin && (
          <button
            className="button button--primary"
            type="submit"
            disabled={systemSettingsMut.isPending}
          >
            Save recipients
          </button>
        )}
      </form>

      <section className="admin-form">
        <h3 className="admin-form__title">Retention policy</h3>
        <p className="admin-section__lede">
          Audit log: <code>{retentionPolicy.audit_log}</code> · Snapshots:{' '}
          <code>{retentionPolicy.snapshots}</code>
        </p>
      </section>

      <section className="admin-form">
        <h3 className="admin-form__title">Sign-in</h3>
        <p className="admin-section__lede">{signInNote || 'No sign-in note configured.'}</p>
      </section>

      <section className="admin-form">
        <h3 className="admin-form__title">Feature switches</h3>
        {!systemAdmin && (
          <p className="admin-section__lede">
            View only — only system administrators can change feature switches.
          </p>
        )}
        <ul className="admin-switch-list">
          {switchEntries.map(([key, enabled]) => (
            <li key={key} className="admin-switch-list__item">
              <label className="admin-switch-list__label">
                <input
                  type="checkbox"
                  checked={enabled}
                  disabled={!systemAdmin || switchesMut.isPending}
                  onChange={(e) => {
                    const next = { ...localSwitches, [key]: e.target.checked }
                    setLocalSwitches(next)
                    if (systemAdmin) {
                      switchesMut.mutate({ [key]: e.target.checked })
                    }
                  }}
                />
                <code>{key}</code>
              </label>
              <span className={enabled ? 'badge badge--status-on' : 'badge badge--status-off'}>
                {enabled ? 'On' : 'Off'}
              </span>
            </li>
          ))}
          {switchEntries.length === 0 && !switchesQuery.isLoading && (
            <li className="empty">No feature switches defined.</li>
          )}
        </ul>
      </section>

      {systemAdmin && aiLocal && (
        <section className="admin-form">
          <h3 className="admin-form__title">AI layer</h3>
          <p className="admin-section__lede">
            Kill switch, Anthropic (Claude) API key, and model names for intent / extract /
            interpret. Leave the master switch off until go-live checklist is recorded.
          </p>
          <ul className="admin-switch-list">
            <li className="admin-switch-list__item">
              <label className="admin-switch-list__label">
                <input
                  type="checkbox"
                  checked={aiLocal.ai_enabled}
                  disabled={aiMut.isPending}
                  onChange={(e) => {
                    const next = { ...aiLocal, ai_enabled: e.target.checked }
                    setAiLocal(next)
                    aiMut.mutate({ ai_enabled: e.target.checked })
                  }}
                />
                <span>AI enabled (master)</span>
              </label>
              <span
                className={
                  aiLocal.ai_enabled ? 'badge badge--status-on' : 'badge badge--status-off'
                }
              >
                {aiLocal.ai_enabled ? 'On' : 'Off'}
              </span>
            </li>
            {channelEntries.map(([key, enabled]) => (
              <li key={key} className="admin-switch-list__item">
                <label className="admin-switch-list__label">
                  <input
                    type="checkbox"
                    checked={enabled}
                    disabled={aiMut.isPending}
                    onChange={(e) => {
                      const channels = { ...aiLocal.channels, [key]: e.target.checked }
                      const next = { ...aiLocal, channels }
                      setAiLocal(next)
                      aiMut.mutate({ channels })
                    }}
                  />
                  <code>{key}</code>
                </label>
                <span className={enabled ? 'badge badge--status-on' : 'badge badge--status-off'}>
                  {enabled ? 'On' : 'Off'}
                </span>
              </li>
            ))}
            <li className="admin-switch-list__item">
              <label className="admin-switch-list__label">
                <input
                  type="checkbox"
                  checked={Boolean(aiLocal.ask_freeform)}
                  disabled={aiMut.isPending || !aiLocal.ai_enabled || !aiLocal.channels?.ask}
                  onChange={(e) => {
                    const next = { ...aiLocal, ask_freeform: e.target.checked }
                    setAiLocal(next)
                    aiMut.mutate({ ask_freeform: e.target.checked })
                  }}
                />
                <span>Ask freeform (read-only tools + NL)</span>
              </label>
              <span
                className={
                  aiLocal.ask_freeform ? 'badge badge--status-on' : 'badge badge--status-off'
                }
              >
                {aiLocal.ask_freeform ? 'On' : 'Off'}
              </span>
            </li>
          </ul>
          <p className="admin-section__lede" style={{ marginTop: '0.5rem' }}>
            Freeform Ask stays off until CR-ask-freeform go-live rows are accepted. Requires master
            AI + <code>ask</code> channel. Catalogue Ask still works when freeform is off. Tool
            allow-list is empty by default (all registered read tools); set{' '}
            <code>ask_freeform_tools</code> via API to restrict.
          </p>

          <div className="admin-form__grid" style={{ marginTop: '1rem' }}>
            <label className="field">
              <span className="field__label">Provider</span>
              <select
                className="field__input"
                value={aiLocal.provider ?? 'mock'}
                disabled={aiMut.isPending}
                onChange={(e) => {
                  const provider = e.target.value as AiSystemSettings['provider']
                  setAiLocal({ ...aiLocal, provider })
                  aiMut.mutate({ provider })
                }}
              >
                <option value="mock">Mock (deterministic, no network)</option>
                <option value="anthropic">Anthropic Claude (API key)</option>
              </select>
            </label>

            <label className="field">
              <span className="field__label">
                Anthropic API key
                {aiLocal.api_key_configured ? ' (configured — leave blank to keep)' : ''}
              </span>
              <input
                className="field__input"
                type="password"
                autoComplete="off"
                placeholder={
                  aiLocal.api_key_configured ? '•••••••• (enter new key to replace)' : 'sk-ant-…'
                }
                disabled={aiMut.isPending}
                onBlur={(e) => {
                  const value = e.target.value
                  if (!value) return
                  aiMut.mutate(
                    { api_key: value },
                    {
                      onSuccess: () => {
                        e.target.value = ''
                      },
                    },
                  )
                }}
              />
              {aiLocal.api_key_configured ? (
                <button
                  type="button"
                  className="button button--ghost button--small"
                  disabled={aiMut.isPending}
                  onClick={() => aiMut.mutate({ api_key: '' })}
                >
                  Clear API key
                </button>
              ) : null}
            </label>

            <label className="field">
              <span className="field__label">Intent model</span>
              <input
                className="field__input"
                value={aiLocal.deployments.intent}
                disabled={aiMut.isPending}
                onChange={(e) =>
                  setAiLocal({
                    ...aiLocal,
                    deployments: { ...aiLocal.deployments, intent: e.target.value },
                  })
                }
                onBlur={(e) =>
                  aiMut.mutate({
                    deployments: { ...aiLocal.deployments, intent: e.target.value },
                  })
                }
              />
            </label>
            <label className="field">
              <span className="field__label">Extract model</span>
              <input
                className="field__input"
                value={aiLocal.deployments.extract}
                disabled={aiMut.isPending}
                onChange={(e) =>
                  setAiLocal({
                    ...aiLocal,
                    deployments: { ...aiLocal.deployments, extract: e.target.value },
                  })
                }
                onBlur={(e) =>
                  aiMut.mutate({
                    deployments: { ...aiLocal.deployments, extract: e.target.value },
                  })
                }
              />
            </label>
            <label className="field">
              <span className="field__label">Interpret model</span>
              <input
                className="field__input"
                value={aiLocal.deployments.interpret}
                disabled={aiMut.isPending}
                onChange={(e) =>
                  setAiLocal({
                    ...aiLocal,
                    deployments: { ...aiLocal.deployments, interpret: e.target.value },
                  })
                }
                onBlur={(e) =>
                  aiMut.mutate({
                    deployments: { ...aiLocal.deployments, interpret: e.target.value },
                  })
                }
              />
            </label>
            <label className="field">
              <span className="field__label">Stage A timeout (seconds)</span>
              <input
                className="field__input"
                type="number"
                min={1}
                max={120}
                value={aiLocal.stage_a_timeout_s}
                disabled={aiMut.isPending}
                onChange={(e) =>
                  setAiLocal({
                    ...aiLocal,
                    stage_a_timeout_s: Number(e.target.value) || aiLocal.stage_a_timeout_s,
                  })
                }
                onBlur={(e) => {
                  const stage_a_timeout_s = Math.max(1, Number(e.target.value) || 30)
                  setAiLocal({ ...aiLocal, stage_a_timeout_s })
                  aiMut.mutate({ stage_a_timeout_s })
                }}
              />
              <span className="field__hint">
                Intent + extract must finish within this window. Anthropic usually needs 20–45s.
              </span>
            </label>
            <label className="field">
              <span className="field__label">Stage B timeout (seconds)</span>
              <input
                className="field__input"
                type="number"
                min={1}
                max={120}
                value={aiLocal.stage_b_timeout_s}
                disabled={aiMut.isPending}
                onChange={(e) =>
                  setAiLocal({
                    ...aiLocal,
                    stage_b_timeout_s: Number(e.target.value) || aiLocal.stage_b_timeout_s,
                  })
                }
                onBlur={(e) => {
                  const stage_b_timeout_s = Math.max(1, Number(e.target.value) || 20)
                  setAiLocal({ ...aiLocal, stage_b_timeout_s })
                  aiMut.mutate({ stage_b_timeout_s })
                }}
              />
            </label>
          </div>
        </section>
      )}
    </AdminSection>
  )
}
